import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Check, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DosPaneles } from "@/components/documentos/dos-paneles";
import { PapelDelExtracto } from "@/components/documentos/papel-del-extracto";
import { type Papel, Visor } from "@/components/propuesta/visor";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { api } from "@/lib/api";
import {
  BANCOS,
  type Borrador,
  Campo,
  type CuentaElegible,
  type EmpresaMaestro,
  type FilaExtracto,
  HojaDelExtracto,
  SelectorDeCuenta,
} from "./carga";

export const Route = createFileRoute("/_app/borrador/$borradorId")({
  component: RevisarBorrador,
});

// ===========================================================================
// Revisar un estado de cuenta ANTES de confirmarlo, con el PDF al lado.
//
// Lo pidió Kurt: «cuando se cargue el estado de cuenta, directamente te manda
// esa página, ahí veas de corregir y ya lo cargues». Y tiene razón, porque la
// pantalla de carga tenía el defecto justo al revés de como parecía:
//
// La rejilla YA marcaba en rojo la línea donde se rompe la cadena de saldos, y
// ya traía la columna del saldo calculado. Lo que faltaba era el papel: sin el
// PDF delante, saber que la línea 26 se separa por 23.730,00 no sirve de nada,
// porque para arreglarla hay que leer el número en el estado de cuenta. Eso
// obligaba a confirmar primero para poder ver el PDF en la ficha del extracto,
// que es exactamente al revés: confirmar es lo último, no lo primero.
//
// Aquí es la misma disposición de la ficha del extracto —tabla a la izquierda,
// papel a la derecha, cada uno con su scroll— pero sobre un borrador, que es
// donde todavía se puede corregir sin deshacer nada.
// ===========================================================================

function RevisarBorrador() {
  const { borradorId } = Route.useParams();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const [papel, setPapel] = useState<Papel | null>(null);

  const { data, isPending } = api.enRevision.useQuery(["carga"]);
  const d = data?.status === 200 ? data.body : null;
  const b = (d?.borradores as Borrador[] | undefined)?.find(
    (x) => x.id === borradorId,
  );

  const confirmar = api.confirmarCarga.useMutation();
  const descartar = api.descartarCarga.useMutation();
  const corregir = api.corregirCarga.useMutation();

  // Los mismos campos que la lista, y no un enlace de vuelta a ella.
  //
  // Sin esto, un extracto que llega sin banco reconocible —de los 32 reales,
  // 22 se detectan por el NOMBRE del archivo, así que uno renombrado se queda
  // sin detectar— aterrizaba aquí y no se podía arreglar: la identidad se
  // corregía en la lista y las líneas aquí, y había que ir y volver.
  const { data: maestros } = api.maestros.useQuery(["maestros-carga"], {
    query: {},
  });
  const cuentas: CuentaElegible[] =
    maestros?.status === 200
      ? (maestros.body.filas as EmpresaMaestro[]).flatMap((e) =>
          e.cuentas.map((c) => ({
            id: c.id,
            banco: c.banco,
            numero: c.numero,
            moneda: c.moneda,
            razonSocial: e.razonSocial,
            ruc: e.ruc,
          })),
        )
      : [];

  const corregirCampo = (cambio: Record<string, string | null>) =>
    corregir.mutate(
      { params: { id: borradorId }, body: cambio },
      {
        onSuccess: () => qc.invalidateQueries(),
        onError: () => toast.error("No se pudo corregir"),
      },
    );

  // Cuántas hojas tiene el PDF. El borrador no lo guarda —todavía no es un
  // archivo— así que sale de la última página que trajo una línea. Es lo que
  // hay, y sirve: pasar de la última hoja con movimientos no aporta nada.
  const paginas =
    b && b.filas.length > 0
      ? Math.max(...(b.filas as FilaExtracto[]).map((f) => f.pagina ?? 1))
      : null;

  const volver = () => navegar({ to: "/carga" });

  if (isPending) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!b) {
    return (
      <div className="p-10 text-center">
        <p className="text-muted-foreground text-sm">
          Ese borrador ya no está esperando: o se confirmó, o se descartó.
        </p>
        <Button className="mt-4" variant="outline" onClick={volver}>
          <ArrowLeft className="mr-1.5 size-4" />
          Volver a la carga
        </Button>
      </div>
    );
  }

  const nombre = b.nombre.replace(/^.*\//, "");

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pt-4 pb-2 md:px-6 lg:px-8">
        {/* --- quién es, y qué se puede hacer con él ---------------------- */}
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" onClick={volver}>
            <ArrowLeft className="mr-1.5 size-4" />
            Carga
          </Button>
          <div className="min-w-0">
            <h1 className="truncate font-semibold text-[19px] tracking-tight">
              {b.banco ?? "Sin banco"} {b.cuenta ?? ""}
            </h1>
            <p className="truncate text-[12px] text-muted-foreground">
              {nombre} · {b.movimientos.toLocaleString("es-PE")} movimientos
              {b.desde && b.hasta ? ` · del ${b.desde} al ${b.hasta}` : ""}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                descartar.mutate(
                  { params: { id: b.id }, body: { motivo: null } },
                  {
                    onSuccess: () => {
                      qc.invalidateQueries();
                      volver();
                    },
                  },
                )
              }
            >
              <X className="mr-1.5 size-3.5" />
              Descartar
            </Button>
            <Button
              size="sm"
              disabled={!b.listo || confirmar.isPending}
              onClick={() =>
                confirmar.mutate(
                  { params: { id: b.id }, body: { confirmo: true } },
                  {
                    onSuccess: (r: { body: { dice?: string } }) => {
                      toast.success(r.body.dice ?? "Confirmado");
                      qc.invalidateQueries();
                      volver();
                    },
                    onError: () => toast.error("No se pudo confirmar"),
                  },
                )
              }
            >
              <Check className="mr-1.5 size-3.5" />
              Confirmar y cargar
            </Button>
          </div>
        </div>

        {/* --- el cuadre, en una franja que no hay que ir a buscar -------- */}
        {b.falta ? (
          <p className="flex shrink-0 items-center gap-1.5 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[12.5px] text-amber-700 dark:text-amber-400">
            <AlertTriangle className="size-4 shrink-0" />
            {b.falta}
          </p>
        ) : null}

        {/* --- quién es este extracto, corregible aquí mismo ------------- */}
        <div className="grid shrink-0 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Campo titulo="Banco">
            <select
              className="w-full rounded border bg-background px-2 py-1 text-[12.5px]"
              value={b.banco ?? ""}
              onChange={(e) => corregirCampo({ banco: e.target.value || null })}
            >
              <option value="">— sin detectar —</option>
              {BANCOS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-muted-foreground">
              {b.origenBanco === "texto"
                ? "lo dice el propio extracto"
                : b.origenBanco === "nombre"
                  ? "sale del nombre del archivo — conviene mirarlo"
                  : "no se pudo detectar"}
            </span>
          </Campo>

          <Campo titulo="Moneda">
            <select
              className="w-full rounded border bg-background px-2 py-1 text-[12.5px]"
              value={b.moneda ?? ""}
              onChange={(e) =>
                corregirCampo({ moneda: e.target.value || null })
              }
            >
              <option value="">— sin detectar —</option>
              <option value="PEN">Soles</option>
              <option value="USD">Dólares</option>
            </select>
          </Campo>

          <Campo titulo="Cuenta de destino">
            <SelectorDeCuenta
              cuentas={cuentas}
              cuentaId={b.cuentaId}
              numero={b.cuenta}
              empresa={b.empresa}
              banco={b.banco}
              moneda={b.moneda}
              alElegir={(id) => corregirCampo({ cuentaId: id })}
            />
            {b.porQueLaCuenta ? (
              <span className="block text-[11px] text-muted-foreground">
                {b.porQueLaCuenta}
              </span>
            ) : null}
          </Campo>
        </div>

        {/* NO va aquí una franja con el cuadre.
            Estuvo, y era peor que nada: enseñaba el cuadre GUARDADO mientras
            la rejilla enseña el de lo que se está escribiendo, así que al
            corregir una línea la franja seguía en verde diciendo «cuadra» y
            tres centímetros más abajo la rejilla decía «la cadena se rompe en
            7 líneas». Dos cifras que se contradicen en la misma pantalla no
            son un adorno: obligan a adivinar cuál manda.

            La barra de la rejilla ya lo dice, y en vivo. */}

        {/* --- la tabla y el papel, cada uno con su scroll ---------------- */}
        <DosPaneles
          clave="ancho-borrador"
          derecha={
            <PapelDelExtracto
              archivoId={b.id}
              paginas={paginas}
              titulo="El PDF que se acaba de subir"
              alAgrandar={(pagina) =>
                setPapel({
                  clase: "expediente",
                  archivoId: b.id,
                  pagina,
                  paginas,
                  titulo: nombre,
                })
              }
            />
          }
          izquierda={
            <div className="min-h-0 flex-1 overflow-auto">
              <HojaDelExtracto
                key={`${b.id}-${b.filas.length}`}
                borradorId={b.id}
                iniciales={b.filas as FilaExtracto[]}
                yaTocadas={b.filasTocadas}
                declaradoAbonos={b.cuadre?.declaradoAbonos ?? null}
                declaradoCargos={b.cuadre?.declaradoCargos ?? null}
                abiertaDeEntrada
                alGuardar={() => qc.invalidateQueries()}
                alLlegarHoja={() => qc.invalidateQueries()}
              />
            </div>
          }
        />
      </div>

      {papel ? <Visor papel={papel} alCerrar={() => setPapel(null)} /> : null}
    </>
  );
}
